/**
 * Paginator — first molecule in the project. Pure dumb component;
 * page state is owned by the consuming ViewModel.
 *
 * Per SOP 02 §"List Pagination (LOCKED — 2026-05-13)":
 *   < md (375–767px)  : "◀ Prev   Page X of N   Next ▶" (no numeric buttons)
 *   md+ (≥ 768px)     : "◀ Prev  1 [2] 3 … N  Next ▶"
 *   Touch targets ≥ 44 × 44
 *   HIDE entirely when total ≤ pageSize (no decorative chrome)
 */

interface PaginatorProps {
  readonly page: number
  readonly pageSize: number
  readonly total: number
  readonly onPageChange: (page: number) => void
  readonly disabled?: boolean
}

const MAX_NUMERIC_BUTTONS_DESKTOP = 7

export function Paginator({
  page,
  pageSize,
  total,
  onPageChange,
  disabled = false
}: PaginatorProps): JSX.Element | null {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  if (total <= pageSize) return null

  const isFirst = page <= 1
  const isLast = page >= totalPages

  const go = (p: number): void => {
    if (disabled) return
    const clamped = Math.min(totalPages, Math.max(1, p))
    if (clamped !== page) onPageChange(clamped)
  }

  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-between sm:justify-center gap-2 mt-6 font-body text-body-sm"
    >
      <button
        type="button"
        onClick={() => go(page - 1)}
        disabled={disabled || isFirst}
        aria-label="Previous page"
        className="h-11 px-3 rounded-md border border-border bg-bg-primary text-charcoal hover:bg-parchment/60 disabled:opacity-50 disabled:hover:bg-bg-primary disabled:cursor-not-allowed transition"
      >
        ◀ Prev
      </button>

      {/* Mobile: text status, hidden on md+ */}
      <span className="sm:hidden text-text-secondary">
        Page {page} of {totalPages}
      </span>

      {/* Desktop: numeric buttons, hidden on < md */}
      <ol className="hidden sm:flex items-center gap-1" role="list">
        {buildNumericRange(page, totalPages, MAX_NUMERIC_BUTTONS_DESKTOP).map((entry, i) =>
          entry === 'ellipsis' ? (
            <li key={`ellipsis-${i}`} className="px-2 text-text-secondary" aria-hidden>
              …
            </li>
          ) : (
            <li key={entry}>
              <button
                type="button"
                onClick={() => go(entry)}
                disabled={disabled}
                aria-label={`Go to page ${entry}`}
                aria-current={entry === page ? 'page' : undefined}
                className={`h-11 min-w-11 px-3 rounded-md border transition ${
                  entry === page
                    ? 'bg-forest text-text-light border-forest'
                    : 'bg-bg-primary text-charcoal border-border hover:bg-parchment/60'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {entry}
              </button>
            </li>
          )
        )}
      </ol>

      <button
        type="button"
        onClick={() => go(page + 1)}
        disabled={disabled || isLast}
        aria-label="Next page"
        className="h-11 px-3 rounded-md border border-border bg-bg-primary text-charcoal hover:bg-parchment/60 disabled:opacity-50 disabled:hover:bg-bg-primary disabled:cursor-not-allowed transition"
      >
        Next ▶
      </button>
    </nav>
  )
}

/**
 * Build the numeric button sequence for the desktop variant.
 * Returns up to `maxButtons` entries, with "ellipsis" markers when
 * the range is collapsed. Always includes page 1 + totalPages.
 *
 * Example output (totalPages=10, current=5, max=7):
 *   [1, 'ellipsis', 4, 5, 6, 'ellipsis', 10]
 */
function buildNumericRange(
  current: number,
  totalPages: number,
  maxButtons: number
): Array<number | 'ellipsis'> {
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const pages = new Set<number>([1, totalPages, current, current - 1, current + 1])
  // Always show 2 + (totalPages - 1) for context near edges.
  pages.add(2)
  pages.add(totalPages - 1)
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b)
  const out: Array<number | 'ellipsis'> = []
  let prev = 0
  for (const p of sorted) {
    if (p - prev > 1) out.push('ellipsis')
    out.push(p)
    prev = p
  }
  return out
}
